import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Badge, Button, DataTable, Input, Status, formatDate, formatRelative, segmentedItemClass, segmentedListClass, type ColumnDef } from '@buildbee/ui';
import { trpc, type RouterOutputs } from '../../lib/trpc';

type Filter = 'all' | 'locked' | 'super_admin' | 'unverified';
type Person = RouterOutputs['platform']['users']['list']['items'][number];

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'locked', label: 'Locked' },
  { value: 'super_admin', label: 'Super admins' },
  { value: 'unverified', label: 'Email not verified' },
];

/** Account state as a Field Ledger status. */
export function AccountStatus({ person }: { person: Pick<Person, 'lockedAt' | 'emailVerified'> }) {
  if (person.lockedAt) return <Status tone="blocked">Locked</Status>;
  if (!person.emailVerified) return <Status tone="risk">Email not verified</Status>;
  return <Status tone="done">Active</Status>;
}

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** /admin/users: everyone with a BuildBee account, searchable by name or email. */
export function PlatformUsersPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [text, setText] = useState('');
  const search = useDebounced(text.trim());
  const query = trpc.platform.users.list.useInfiniteQuery(
    { filter, search: search || undefined, limit: 50 },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const rows = query.data?.pages.flatMap((p) => p.items);

  const columns: ColumnDef<Person, unknown>[] = [
    {
      id: 'name',
      accessorFn: (u) => `${u.name} ${u.email}`,
      header: 'Person',
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.name}</div>
          <div className="text-xs text-muted-foreground">{row.original.email}</div>
        </div>
      ),
    },
    { id: 'companies', accessorKey: 'companies', header: 'Companies', cell: ({ row }) => <span className="figures">{row.original.companies}</span> },
    {
      id: 'platformRole',
      accessorKey: 'platformRole',
      header: 'Access',
      cell: ({ row }) => (row.original.platformRole === 'super_admin' ? <Badge variant="primary">Super admin</Badge> : <span className="text-muted-foreground">Customer</span>),
    },
    { id: 'status', header: 'Status', cell: ({ row }) => <AccountStatus person={row.original} /> },
    {
      id: 'lastSeenAt',
      accessorKey: 'lastSeenAt',
      header: 'Last seen',
      cell: ({ row }) => row.original.lastSeenAt ? <span>{formatRelative(row.original.lastSeenAt)}</span> : <span className="text-muted-foreground">Never</span>,
    },
    { id: 'createdAt', accessorKey: 'createdAt', header: 'Joined', cell: ({ row }) => <span className="figures">{formatDate(row.original.createdAt)}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          aria-label="Search people"
          placeholder="Search by name or email"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="sm:max-w-xs"
        />
        <div role="group" aria-label="Show" className={segmentedListClass}>
          {FILTERS.map((f) => (
            <button key={f.value} type="button" aria-pressed={filter === f.value} className={segmentedItemClass(filter === f.value)} onClick={() => setFilter(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        loading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        searchable={false}
        paginate={false}
        getRowId={(u) => u.id}
        onRowClick={(u) => void navigate({ to: '/admin/users/$userId', params: { userId: u.id } })}
        empty={{ title: 'Nobody matches', description: search ? 'Try part of the email address instead.' : undefined }}
        footer={
          query.hasNextPage ? (
            <Button variant="outline" size="sm" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              Load more
            </Button>
          ) : null
        }
      />
    </div>
  );
}
